/**
 * @module features/navigation/breadcrumb
 * @description Breadcrumb navigation component
 * Manages breadcrumb display for entity navigation hierarchy
 */

import { ENTITY_TYPES } from '../entity/entity-types.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { logger } from '../../utils/logger.js';

/**
 * Create or get the breadcrumb container element
 * @returns {HTMLElement} The breadcrumb container element
 */
export function createBreadcrumbContainer() {
    let container = document.getElementById('breadcrumb-container');
    if (!container) {
        container = document.createElement('nav');
        container.id = 'breadcrumb-container';
        container.className = 'breadcrumb-container hidden';

        // Insert after header
        const header = document.querySelector('header');
        if (header && header.parentNode) {
            header.parentNode.insertBefore(container, header.nextSibling);
        }
    }
    return container;
}

/**
 * Update breadcrumb navigation with current entity information
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {string} entityName - Display name of the entity
 * @param {string} _entityId - ID of the entity
 */
export function updateBreadcrumb(entityType, entityName, _entityId) {
    const container = createBreadcrumbContainer();
    const config = ENTITY_TYPES[entityType];

    if (!config) {
        logger.warn('Invalid entity type for breadcrumb:', entityType);
        return;
    }

    container.innerHTML = `
        <div class="breadcrumb">
            <a href="?" class="breadcrumb-link breadcrumb-home">
                <span class="breadcrumb-icon">🏠</span>
                Archive Home
            </a>
            <span class="breadcrumb-separator">›</span>
            <a href="?type=${entityType}" class="breadcrumb-link breadcrumb-entity-type" data-entity-type="${entityType}">
                <span class="breadcrumb-icon">${config.icon}</span>
                ${config.label}s
            </a>
            <span class="breadcrumb-separator">›</span>
            <span class="breadcrumb-current">
                ${escapeHtml(entityName)}
            </span>
        </div>
    `;

    // Add event listener for home link
    const homeLink = container.querySelector('.breadcrumb-home');
    if (homeLink) {
        homeLink.addEventListener('click', (e) => {
            e.preventDefault();
            // Navigate to home - let the router handle it via URL change
            window.history.pushState({}, '', '?');
            window.location.reload(); // Simple reload to show entity selector
        });
    }

    // Add event listener for entity type link
    const entityTypeLink = container.querySelector('.breadcrumb-entity-type');
    if (entityTypeLink) {
        entityTypeLink.addEventListener('click', (e) => {
            e.preventDefault();
            const type = entityTypeLink.dataset.entityType;
            // Navigate to entity type search
            window.history.pushState({}, '', `?type=${type}`);
            window.location.reload(); // Reload to show entity search
        });
    }

    container.classList.remove('hidden');
}

/**
 * Hide the breadcrumb navigation
 */
export function hideBreadcrumb() {
    const container = document.getElementById('breadcrumb-container');
    if (container) {
        container.classList.add('hidden');
    }
}

/**
 * Show entity type breadcrumb (for entity type selection pages)
 * Shows Home > Entity Type navigation
 * @param {string} entityType - Type of entity
 */
export function showEntityTypeBreadcrumb(entityType) {
    const container = createBreadcrumbContainer();
    const config = ENTITY_TYPES[entityType];

    if (!config) {
        logger.warn('Invalid entity type for breadcrumb:', entityType);
        return;
    }

    container.innerHTML = `
        <div class="breadcrumb">
            <a href="?" class="breadcrumb-link breadcrumb-home">
                <span class="breadcrumb-icon">🏠</span>
                Archive Home
            </a>
            <span class="breadcrumb-separator">›</span>
            <span class="breadcrumb-current">
                <span class="breadcrumb-icon">${config.icon}</span>
                ${config.labelPlural || `${config.label  }s`}
            </span>
        </div>
    `;

    // Add event listener for home link
    const homeLink = container.querySelector('.breadcrumb-home');
    if (homeLink) {
        homeLink.addEventListener('click', (e) => {
            e.preventDefault();
            window.history.pushState({}, '', '?');
            window.location.reload();
        });
    }

    container.classList.remove('hidden');
}

/**
 * Show breadcrumb navigation
 */
export function showBreadcrumb() {
    const container = document.getElementById('breadcrumb-container');
    if (container) {
        container.classList.remove('hidden');
    }
}

/**
 * Update breadcrumb with custom content
 * @param {string} htmlContent - HTML content for the breadcrumb
 */
export function updateBreadcrumbHTML(htmlContent) {
    const container = createBreadcrumbContainer();
    container.innerHTML = htmlContent;
    container.classList.remove('hidden');
}

export default {
    createBreadcrumbContainer,
    updateBreadcrumb,
    hideBreadcrumb,
    showEntityTypeBreadcrumb,
    showBreadcrumb,
    updateBreadcrumbHTML
};
